import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContextProvider from "../../utils/ContextApi";
import { useLocation } from 'react-router-dom';
import PageBase from "../PageBase";
import bullet from '../../assets/title-bullet.png';
import FieldGroup from "../FieldGroup/index";
import ObjectSummaryRow from "../ObjectSummaryRow"
import './EntityEditor.scss';
import { loadContacts, loadContactByVendorAndId } from "../../redux/Contact/actions";
import Button from "../../components/Button";
import * as userActions from "../../redux/User/actions";
import AccountManagerReportPopup from '../../pages/Mapping/AccountManagerReportPopup';
import Field from '../FieldGroup/Field';
import { useParams } from 'react-router-dom';
import Toaster from "../../components/Toaster";
import { getFilteredFieldsByVendor } from '../../pages/Accounts/viewAccountColumn';
import { columns } from '../../pages/Contacts/EditContact/viewContactColumn';
import { getObjectFields } from '../../pages/Settings/DBSnapshots/LoadData/fields'
import * as ROUTES from '../../utils/routes';

/**
 * CRM Entity Detail Viewer with Relationship Management
 * 
 * Domain Logic:
 * - Displays detailed view of CRM entities (contacts or accounts)
 * - Shows entity relationships (contacts within accounts, accounts for contacts)
 * - Vendor-specific field configurations and data loading
 * - Dynamic field grouping based on entity type and vendor
 * - Relationship reporting with filtered data views
 * 
 * @param {Object} props - Component props
 */
const EntityEditor = props => {
	const location = useLocation();
	const isContact = location.pathname.includes('contact');
	const params = useParams();
	const { vendor, id } = params;
	
	// State for entity data and loading
	/** @type {boolean} Loading state for entity data */
	const [loading, setLoading] = useState(true);
	/** @type {Object} Current entity data (contact or account) */
	const [rowData, setRowData]=useState(location.state?.row);
	
	const { accountManagerReportData, setAccountManagerReportData, setHeader } = useContext(ContextProvider);
	const dispatch = useDispatch();
	
	// Get contact data from Redux store (similar to EditContact)
	const contacts = useSelector(state => state.contact);
	let contactFromStore;
	if (isContact) {
		if(vendor === 'aws') {
			contactFromStore = contacts.awsContacts?.find(c => c.contact_id.toString() === id);
		}
		else {
			contactFromStore = contacts.msContacts?.find(c => c.contact_id.toString() === id);
		}
	}
	
	const headerLinkText = location.state?.headerLinkTexts;
	const headerLinkTarget = location.state?.headerLinkTarget;
	const title = isContact? "Contact":"Account";
	
	/**
	 * Configuration for relationship reporting
	 * Determines which related entities to show based on current entity type
	 */
	const report = isContact?	{field: `contact_name`, value: rowData?.contact_name, relatedTable: 'accounts'}:{
		field: `${vendor}_account_name`,
		value: rowData?.account_name,
		relatedTable: "contacts",
	 }
	// const headerLinkTarget = isContact ? ROUTES.CONTACTS : ROUTES.ACCOUNTS;
console.log(isContact);

	useEffect(() => {
		const filters= [{key: isContact?'contact_id':'account_id', operator: {key: 'is_exact'}, value: id}] 
		const load = async () => {
			setLoading(true);
			if(!isContact){
				 dispatch(userActions.getVendorAccounts(vendor, 1, null, filters, data => setRowData(data.accounts
					? data.accounts[0]
					: null
				)));
			}else{
				// Always call loadContactByVendorAndId for contact detail to get complete data
				dispatch(loadContactByVendorAndId(vendor, id));
			}
			setLoading(false);
		};
		load();
	}, []);

	// Update rowData when contact is loaded from store
	useEffect(() => {
		if (isContact && contactFromStore) {
			// Always use the data from store (complete contact details) instead of location.state
			setRowData(contactFromStore);
		}
	}, [contactFromStore, isContact]);

	/**
	 * Loads related entities based on current entity type
	 * Shows contacts within accounts or accounts for contacts
	 */
	const showManagedAccounts = () => {
		const field = report.field;
		const value = report.value;
		const reportHeader = { name: `${title} Name`, value: value, tableName: report.relatedTable }
		if (title === 'Contact') {
			const rows =[ [{ key: 'contact_id', field:'contact_id', type: "exact", value: id }]];
			dispatch(userActions.runReport(vendor, rows, data => accountManagerReportLoaded(data.data || [], value, reportHeader)));
		} else if (title === 'Account') {
			const filterByAccountId =  [{
				key: "account_id",
				value: id,
				operator: {
					key: "is_exact",
					selected: true,
				}}];
			dispatch(loadContacts(vendor, null, 0, false, filterByAccountId, data => accountManagerReportLoaded(data.contacts || [], value, reportHeader)));
		}

	};

	/**
	 * Handles loaded relationship data and shows popup
	 * Displays warning if no related entities found
	 */
	const accountManagerReportLoaded = (result, accountManager, reportHeader) => {
		if (result.length === 0) {
			Toaster.showWarn(`No Related ${report.relatedTable} Found`);
			return;
		}

		setLoading(false);
		setHeader(reportHeader)
		setAccountManagerReportData(result);
	};

	if (rowData == null) {
		return <div />;
	}

	/**
	 * Summary data for entity overview
	 * Shows key fields based on entity type (contact vs account)
	 */
	const summary = title === 'Account' ? [
		{ title: 'Account Name', value: rowData?.account_name, blue: true },
		{ title: 'Address', value: rowData?.address },
		{ title: 'Country Name', value: rowData?.hq },
		{ title: 'Industry Name', value: rowData?.industry },
	] : [
		{ title: 'Contact Name', value: rowData?.contact_name, blue: true },
		{ title: 'Title', value: rowData?.title },
		{ title: 'Email', value: rowData?.contact_email },
		{ title: 'Accounts Type', value: rowData?.accounts_type_name },
	];
	
	/**
	 * Vendor-specific field configurations
	 * Dynamically loads field definitions based on entity type and vendor
	 */
	const fieldColumns = title === 'Account' ? getFilteredFieldsByVendor(vendor) : columns;
	const ObjectFields = getObjectFields(vendor, `${title.toLowerCase()}s`)

	return (
		<PageBase title={title}
			removeMargins
			hideRefresh
			bodyBg="#fff"
			headerLinkText={headerLinkText}
			headerLinkTarget={headerLinkTarget}
		>

			{
				accountManagerReportData != null &&
				<AccountManagerReportPopup />
			}

			<div className="edit-contact-title">
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<img src={bullet} style={{ width: '24px', marginRight: '20px' }} />
					<span>{title}</span>
				</div>

				<div className="edit-contact-buttons">
					<Button onClick={showManagedAccounts} type="blue" center>Related {report.relatedTable.charAt(0).toUpperCase() + report.relatedTable.slice(1)}</Button>
				</div>
			</div>

			<ObjectSummaryRow data={summary} />

			<div style={{ height: '100%', overflowY: 'auto', padding: '0 20px' }}>
				{fieldColumns.map(({ title, fields }) => (
					<FieldGroup key={title} title={title} expandable expanded>
						{fields.map((key, i) => (
							<Field key={i} title={ObjectFields.find(item => item.value === key)?.label}>
								{rowData[key]}
							</Field>
						))}
					</FieldGroup>
				))} 			</div>
		</PageBase>
	)
};

export default EntityEditor;
